import { createRoot } from 'react-dom/client';
import App from './App';
import './index.css';

// Disable browser's automatic scroll restoration to prevent conflicts
if ('scrollRestoration' in history) {
  history.scrollRestoration = 'manual';
}

// Disable browser's automatic scroll restoration to prevent conflicts
if ('scrollRestoration' in history) {
  history.scrollRestoration = 'manual';
}

import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';

// Add a class to <html> only when running in the native shell
if (Capacitor.isNativePlatform()) {
  document.documentElement.classList.add('cap-native');
  document.documentElement.classList.add('debug-safe');
  // Optional band for safe area visualization:
  // document.documentElement.classList.add('show-safe-area');
}

// Keep cleanup across HMR
declare global {
  interface Window {
    __resumeListenerCleanup?: () => void;
    __appStateListenerCleanup?: () => void;
    __visibilityChangeCleanup?: () => void;
  }
}

// Apply non-overlay status bar and matching colors/icons
const applyStatusBar = async () => {
  if (!Capacitor.isNativePlatform()) return;
  try {
    // On Android 16, ensure we set color BEFORE overlay setting to avoid conflicts
    // Match your header color FIRST (primary-600: #0A2463)
    await StatusBar.setBackgroundColor({ color: '#0A2463' });
    // Keep the WebView BELOW the Android status bar
    await StatusBar.setOverlaysWebView({ overlay: false });
    // ✅ White icons on dark background (Android = DARK => light/white icons)
    await StatusBar.setStyle({ style: Style.Dark });
    // Set color again after overlay setting (Android 16 sometimes resets it)
    // Note: Using hex value here as StatusBar API requires hex, but this matches primary-600 theme color
    await StatusBar.setBackgroundColor({ color: '#0A2463' });
  } catch {
    // Ignore if plugin not available (web) or platform quirks
  }
};

// Aggressive reapply helper - applies immediately and with delays to catch Android resets
const reapplyAfterWake = () => {
  // Apply immediately (no delay)
  applyStatusBar();
  // Also apply after short delays to catch Android resets that happen after resume
  setTimeout(applyStatusBar, 100);
  setTimeout(applyStatusBar, 300);
  setTimeout(applyStatusBar, 600);
};

// Initial apply (slight delay to ensure window is fully shown)
reapplyAfterWake();

// Re-apply on resume / when app becomes active (OEMs may reset styling)
if (Capacitor.isNativePlatform()) {
  try { window.__resumeListenerCleanup?.(); } catch { /* ignore */ }
  try { window.__appStateListenerCleanup?.(); } catch { /* ignore */ }
  try { window.__visibilityChangeCleanup?.(); } catch { /* ignore */ }

  // Listen for app resume (when app comes back from background)
  CapacitorApp.addListener('resume', reapplyAfterWake).then(handle => {
    window.__resumeListenerCleanup = () => {
      try { handle.remove?.(); } catch { /* ignore */ }
      window.__resumeListenerCleanup = undefined;
    };
  });

  // Listen for app state changes
  CapacitorApp.addListener('appStateChange', ({ isActive }) => {
    if (isActive) reapplyAfterWake();
  }).then(handle => {
    window.__appStateListenerCleanup = () => {
      try { handle.remove?.(); } catch { /* ignore */ }
      window.__appStateListenerCleanup = undefined;
    };
  });

  // Also listen for page visibility changes (catches wake from sleep)
  const handleVisibilityChange = () => {
    if (!document.hidden) {
      reapplyAfterWake();
    }
  };
  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.__visibilityChangeCleanup = () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.__visibilityChangeCleanup = undefined;
  };
}

// Remove listeners on HMR dispose (dev only)
if (import.meta?.hot) {
  import.meta.hot.dispose(() => {
    try { window.__resumeListenerCleanup?.(); } catch { /* ignore */ }
    try { window.__appStateListenerCleanup?.(); } catch { /* ignore */ }
    try { window.__visibilityChangeCleanup?.(); } catch { /* ignore */ }
  });
}

createRoot(document.getElementById('root')!).render(
  <App />
);